/**
 * User administration data access.
 *
 * Everything here is a thin, typed call into the generated client — no hand
 * written URLs, no `fetch`. The only logic that lives at this layer is the
 * cursor walk: several screens need "all users matching this filter" (an export,
 * a headcount, the holders of a role), and §5 caps a page at 200 rows, so the
 * walk is written once, bounded, and shared.
 */

import {
  rolesList,
  usersCreate,
  usersDisable,
  usersEnable,
  usersList,
  usersResendInvite,
  usersRolesUpdate,
  usersServicePrincipalsCreate,
  usersUpdate,
  type CreateUserRequest,
  type CreateUserResponse,
  type ListUsersQuery,
  type PageUserDTO,
  type RoleDTO,
  type UserDTO,
} from '@erpx/api'

import { api } from '@/api/client'
import { MAX_LIMIT } from '@/composables/useTableUrlState'
import { i18n, t } from '@/i18n'

export interface UserFilters {
  q: string
  status: string
  limit: number
  cursor: string
}

export type UserStatus = 'invited' | 'active' | 'disabled'

export const USER_STATUSES: readonly UserStatus[] = ['active', 'invited', 'disabled']

/** Only send parameters that carry meaning; an empty `q` is not a filter. */
function toQuery(filters: Partial<UserFilters>): ListUsersQuery {
  return {
    ...(filters.q === undefined || filters.q === '' ? {} : { q: filters.q }),
    ...(filters.status === undefined || filters.status === '' ? {} : { status: filters.status }),
    ...(filters.limit === undefined ? {} : { limit: filters.limit }),
    ...(filters.cursor === undefined || filters.cursor === '' ? {} : { cursor: filters.cursor }),
  }
}

export function listUsers(filters: Partial<UserFilters>): Promise<PageUserDTO> {
  return usersList(api(), toQuery(filters))
}

/** Hard stop so a runaway cursor cannot spin the browser. 20 × 200 = 4,000 rows. */
const MAX_PAGES = 20

/**
 * Walk the cursor to the end of a filtered result set. Used by the CSV export
 * and by the counts on the dashboard, both of which are about *all* matching
 * rows rather than the page currently on screen.
 */
export async function listAllUsers(filters: Omit<Partial<UserFilters>, 'cursor' | 'limit'>): Promise<{
  users: UserDTO[]
  truncated: boolean
}> {
  const users: UserDTO[] = []
  let cursor = ''

  for (let page = 0; page < MAX_PAGES; page++) {
    const result: PageUserDTO = await listUsers({ ...filters, limit: MAX_LIMIT, cursor })
    users.push(...result.items)
    if (!result.hasMore || result.nextCursor === null || result.nextCursor === '') {
      return { users, truncated: false }
    }
    cursor = result.nextCursor
  }

  return { users, truncated: true }
}

export function listRoles(): Promise<RoleDTO[]> {
  return rolesList(api())
}

/**
 * How the new account gets a credential.
 *
 * The server's vocabulary verbatim (`identity.CredentialMode`), not a
 * client-side alias: the field is a string on the wire and a typo would be a
 * 422 rather than a compile error, so the one place the three spellings appear
 * is here.
 *
 *  - `invite`   — the historical behaviour and the server's default: a link is
 *                 emailed and no password exists until it is redeemed.
 *  - `set`      — the caller supplies the password. For someone who has no
 *                 mailbox yet, or who is standing at the desk.
 *  - `generate` — the server mints one and returns it exactly once.
 */
export type CredentialMode = 'invite' | 'set' | 'generate'

export const CREDENTIAL_MODES: readonly CredentialMode[] = ['invite', 'set', 'generate']

export interface InviteUserInput {
  email: string
  name: string
  roleCodes: string[]
  overrideReason?: string
  credentialMode?: CredentialMode
  /** Required when `credentialMode` is `set`, forbidden otherwise. */
  password?: string
  /** Defaults to true server-side for `set` and `generate`; meaningless for `invite`. */
  mustChangePassword?: boolean
}

/**
 * Create a tenant user.
 *
 * Returns the whole `CreateUserResponse`, not the user: `generatedPassword` is
 * present exactly once, for `generate` only, and is not recoverable afterwards.
 * Unwrapping to `.user` here would throw away the only copy that will ever
 * exist, which is precisely why the server nests it.
 */
export function inviteUser(
  input: InviteUserInput,
  idempotencyKey: string,
): Promise<CreateUserResponse> {
  const mode: CredentialMode = input.credentialMode ?? 'invite'

  // Built key by key rather than spread-with-undefined: under
  // `exactOptionalPropertyTypes` an explicit `undefined` is not an absent
  // property, and the server rejects `password` outright unless the mode is
  // `set` — so a stale value from a mode the user switched away from has to be
  // absent, not empty.
  const body: CreateUserRequest = {
    email: input.email,
    name: input.name,
    roleCodes: input.roleCodes,
    ...(input.overrideReason === undefined || input.overrideReason === ''
      ? {}
      : { overrideReason: input.overrideReason }),
    ...(mode === 'invite' ? {} : { credentialMode: mode }),
    ...(mode === 'set' && input.password !== undefined && input.password !== ''
      ? { password: input.password }
      : {}),
    ...(mode === 'invite' || input.mustChangePassword === undefined
      ? {}
      : { mustChangePassword: input.mustChangePassword }),
  }

  return usersCreate(api(), body, idempotencyKey)
}

export interface UpdateUserInput {
  name?: string
  locale?: string
  timezone?: string
  phone?: string
  jobTitle?: string
  rowVersion: number
}

export function updateUser(id: number, input: UpdateUserInput): Promise<UserDTO> {
  return usersUpdate(
    api(),
    { id: String(id) },
    {
      rowVersion: input.rowVersion,
      ...(input.name === undefined ? {} : { name: input.name }),
      ...(input.locale === undefined ? {} : { locale: input.locale }),
      ...(input.timezone === undefined ? {} : { timezone: input.timezone }),
      ...(input.phone === undefined ? {} : { phone: input.phone }),
      ...(input.jobTitle === undefined ? {} : { jobTitle: input.jobTitle }),
    },
  )
}

export function setUserRoles(
  id: number,
  roleCodes: string[],
  overrideReason?: string,
): Promise<void> {
  return usersRolesUpdate(
    api(),
    { id: String(id) },
    {
      roleCodes,
      ...(overrideReason === undefined || overrideReason === '' ? {} : { overrideReason }),
    },
  )
}

export function disableUser(id: number): Promise<void> {
  return usersDisable(api(), { id: String(id) })
}

export function enableUser(id: number): Promise<void> {
  return usersEnable(api(), { id: String(id) })
}

export function resendInvite(id: number): Promise<void> {
  return usersResendInvite(api(), { id: String(id) })
}

/**
 * A service principal: an account that runs background jobs under its own
 * roles. It has no password, no mailbox and no MFA, and it never signs in, so
 * every person-only action (invite, resend, password, MFA) is meaningless for
 * it. The server's `principalKind` is the only source of truth — an unknown or
 * missing value is treated as a person, the conservative reading for hiding
 * nothing a person needs.
 */
export function isServicePrincipal(user: Pick<UserDTO, 'principalKind'> | null | undefined): boolean {
  return user?.principalKind === 'service'
}

/**
 * Create a service principal (`POST /service-principals`, `admin.user.invite`).
 * The idempotency key is the caller's — one per intent — so a retried request
 * yields one principal, not two (§7).
 */
export function createServicePrincipal(name: string, idempotencyKey: string): Promise<UserDTO> {
  return usersServicePrincipalsCreate(api(), { name }, idempotencyKey)
}

/** Tone for the status badge. Colour means state and nothing else (§11). */
export function statusTone(status: string): 'success' | 'warning' | 'neutral' {
  if (status === 'active') return 'success'
  if (status === 'invited') return 'warning'
  return 'neutral'
}

/**
 * The status as a person reads it. An unknown status is shown as the server
 * spelled it rather than blanked — a new status the client has not learned yet
 * is still information.
 */
export function statusLabel(status: string): string {
  const key = `users.status.${status}`
  return i18n.global.te(key) ? t(key) : status
}
